import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SectionTitle } from './PageShell';
import { AdminGuard } from './AdminPages';
import AdminShell from './AdminShell';
import { api } from '../lib/api';
import { Loader2, GraduationCap, BookOpenCheck, UserCheck } from 'lucide-react';

const ROLE_ICON = { eleve: GraduationCap };
const ROLE_LABEL = { eleve: 'Élève' };

function ProgressBar({ pct }) {
  return (
    <div className="h-2 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-red-600 to-red-400" style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

export default function AdminLivretPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminGlobalLivret().then(setItems).finally(() => setLoading(false));
  }, []);

  const totalStudents = items.length;
  const avgPct = totalStudents ? Math.round(items.reduce((s, i) => s + i.livret_pct, 0) / totalStudents) : 0;
  const fullyDone = items.filter((i) => i.livret_pct >= 100).length;

  return (
    <AdminGuard>
      <AdminShell>
        <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10" data-testid="admin-livret-page">
          <SectionTitle
            kicker="Back-office · Suivi pédagogique global"
            title="Avancée du livret d'apprentissage"
            sub="Vue d'ensemble (lecture seule) de la progression de chaque élève et futur enseignant sur le référentiel REMC."
          />

          {/* KPIs */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec mb-3">Apprenants suivis</div>
              <div className="font-serif-editorial text-white text-[44px] leading-none">{totalStudents}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec mb-3">Progression moyenne</div>
              <div className="font-serif-editorial text-red-500 text-[44px] leading-none">{avgPct}<span className="text-[24px] text-white/40">%</span></div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold font-mono-spec mb-3">100 % validé</div>
              <div className="font-serif-editorial text-emerald-400 text-[44px] leading-none">{fullyDone}</div>
            </div>
          </div>

          {loading ? (
            <div className="text-white/50 inline-flex items-center gap-2 py-10"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid="admin-livret-table">
              <table className="w-full text-[13px]">
                <thead className="bg-white/[0.03] text-white/50 text-[11px] uppercase tracking-wider font-mono-spec">
                  <tr>
                    <th className="text-left p-4">Apprenant</th>
                    <th className="text-left p-4 hidden md:table-cell">Rôle</th>
                    <th className="text-left p-4 hidden lg:table-cell">Enseignant rattaché</th>
                    <th className="text-left p-4">Progression</th>
                    <th className="text-right p-4">Validées</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {items.map((s) => {
                    const Icon = ROLE_ICON[s.role] || GraduationCap;
                    return (
                      <tr key={s.id} className="hover:bg-white/[0.02]" data-testid={`admin-livret-row-${s.id}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-red-600/10 border border-red-700/30 flex items-center justify-center text-red-400 shrink-0">
                              <Icon size={14} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-white font-semibold">{s.name}</div>
                              <div className="text-white/45 text-[11.5px] truncate font-mono-spec">{s.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell text-white/75">{ROLE_LABEL[s.role] || s.role}</td>
                        <td className="p-4 hidden lg:table-cell">
                          {s.teacher ? (
                            <div className="inline-flex items-center gap-1.5 text-white/75 text-[12.5px]">
                              <UserCheck size={12} className="text-red-400" />
                              {s.teacher.name}
                            </div>
                          ) : (
                            <span className="text-white/35 text-[12px] italic">Non rattaché</span>
                          )}
                        </td>
                        <td className="p-4 min-w-[180px]">
                          <ProgressBar pct={s.livret_pct} />
                          <div className="text-[11px] text-white/45 mt-1 font-mono-spec">{s.livret_pct} %</div>
                        </td>
                        <td className="p-4 text-right">
                          <div className="text-white font-serif-editorial text-[20px] leading-none">{s.livret_done}<span className="text-white/30 text-[14px]">/38</span></div>
                          {s.livret_inprog > 0 && <div className="text-[11px] text-amber-400/80 mt-0.5">{s.livret_inprog} en cours</div>}
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-white/45">Aucun apprenant pour l'instant.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-white/35 text-[11.5px] mt-6 font-mono-spec">
            Vue lecture seule · La validation des compétences reste à la main de l'enseignant rattaché.
          </p>
        </section>
      </AdminShell>
    </AdminGuard>
  );
}
